import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ScrollView, StyleSheet, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';

import StoreView from './store/StoreView';
import PurchaseModal from './store/PurchaseModal';
import VaultView from './vault/VaultView';
import RelicDetailModal from './vault/RelicDetailModal';
import { useStorePurchaseFlow } from './hooks/useStorePurchaseFlow';
import { useVaultBrowser } from './hooks/useVaultBrowser';
import { pageBackground } from './shared/theme';
import {
  equipCompanion,
  refreshAllowanceFor,
  refreshStoreSlots,
} from 'store/relicVault';
import { ALL_GUILDS_ID, relicsForGuild } from 'utils/relicVault/vaultSelectors';

/**
 * Full-screen Relic Vault, opened from the metaverse HUD: the Store (relics +
 * guardian companions) and the Vault browser, with their popups layered on top.
 *
 * Ported from the designer's handoff (relic-vault-react). `balance` is the
 * QuikCoins balance to display and spend against - the real balance plus any
 * local spend adjustment (see store/relicVault).
 */
const RelicVaultOverlay = ({ visible, onClose, balance, userId, onServerBalanceChanged }) => {
  const dispatch = useDispatch();
  const vault = useSelector(state => state.relicVault);
  const [view, setView] = useState('store');

  const purchase = useStorePurchaseFlow({ userId, balance, onServerBalanceChanged });
  const browser = useVaultBrowser({ userId, onServerBalanceChanged });

  // Always reopen on the store.
  useEffect(() => {
    if (visible) setView('store');
  }, [visible]);

  const refreshAllowance = refreshAllowanceFor(vault);

  const selectedRelic = useMemo(() => {
    if (!browser.selectedRelicId) return null;
    return (
      relicsForGuild(ALL_GUILDS_ID, vault.ownedRelicIds).find(
        r => r.id === browser.selectedRelicId,
      ) || null
    );
  }, [browser.selectedRelicId, vault.ownedRelicIds]);

  const onRefresh = useCallback(async () => {
    const result = await dispatch(refreshStoreSlots({ userId, balance }));
    if (!result?.success) {
      console.warn('[RelicVault] refresh failed', result?.error);
      return;
    }
    if (result.serverBalanceChanged && onServerBalanceChanged) onServerBalanceChanged();
  }, [balance, dispatch, onServerBalanceChanged, userId]);

  const onEquip = useCallback(
    guildId => dispatch(equipCompanion({ userId, guildId })),
    [dispatch, userId],
  );

  const handleClose = useCallback(() => {
    purchase.close();
    browser.closeDetail();
    onClose && onClose();
  }, [browser, onClose, purchase]);

  return (
    <Modal
      visible={!!visible}
      animationType="fade"
      transparent
      onRequestClose={handleClose}
      supportedOrientations={['portrait', 'landscape', 'landscape-left', 'landscape-right']}>
      <View style={[styles.root, pageBackground]}>
        {view === 'store' ? (
          <ScrollView style={styles.fill} contentContainerStyle={styles.scrollContent}>
            <StoreView
              vault={vault}
              balance={balance}
              refreshAllowance={refreshAllowance}
              purchase={purchase}
              onRefresh={onRefresh}
              onOpenVault={() => setView('vault')}
              onEquip={onEquip}
              onClose={handleClose}
            />
          </ScrollView>
        ) : (
          <VaultView
            vault={vault}
            balance={balance}
            browser={browser}
            onOpenStore={() => setView('store')}
            onClose={handleClose}
          />
        )}

        {view === 'store' && (
          <PurchaseModal
            flow={purchase}
            balance={balance}
            ownedCompanionIds={vault.ownedCompanionIds}
            equippedCompanionId={vault.equippedCompanionId}
          />
        )}
        {view === 'vault' && <RelicDetailModal relic={selectedRelic} browser={browser} />}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  fill: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
  },
});

export default RelicVaultOverlay;
